import * as THREE from 'three';
import { onMessage, OP, me, F_ALIVE, unpackPos } from './net.js';
import { WEAPONS, WEAPON_KEY, cancelReload } from './weapons.js';
import { spawnDamage, showHitmarker, addKill, spawnTracer, spawnSparks, addDecal, spawnBloodSplat } from './fx.js';
import { wallMeshes } from './state.js';
import { playHit, playHurt } from './audio.js';
import { killPlayer, respawnPlayer } from './player.js';
import { resetPrediction } from './prediction.js';
import { remoteList, bodiesAlong } from './remote.js';
import { rememberKill } from './corpse.js';
import { player, S } from './state.js';

// ============================================================
//  Боевые события от сервера.
//
//  Ключевое отличие от прототипа: клиент больше НЕ решает, попал ли он и
//  сколько снял HP. Он рисует выстрел сразу (иначе стрельба ощущалась бы
//  вязкой), но хитмаркер, урон, смерть и killfeed приходят с сервера.
//  Поэтому «поправил HP в devtools» ничего не даёт: своё здоровье клиент
//  не считает, а только отображает.
// ============================================================

/** Ники сетевых игроков для killfeed */
const nicks = new Map();

const _p = new THREE.Vector3();
/** Луч для нормали стены под чужой дыркой от пули (см. OP.SHOT ниже) */
const _ray = new THREE.Raycaster();

/** Позиция чужого игрока — чтобы нарисовать над ним число урона.
 *  ⚠️ Группа стоит СТУПНЯМИ на полу (см. remote.js), а не центром тела,
 *  поэтому смещение отсчитывается от ног: 1.9 — чуть выше макушки. */
function remotePos(id) {
  const r = remoteList().get(id);
  return r ? _p.copy(r.group.position).setY(r.group.position.y + 1.9) : null;
}

onMessage((op, d, buf) => {
  switch (op) {
    // ---- Мы попали ----
    case OP.HIT: {
      const target = d.getUint16(1, true);
      const flags = d.getUint8(3);
      const dmg = d.getUint8(4);
      const head = (flags & 1) !== 0;
      showHitmarker();
      playHit(head);
      const p = remotePos(target);
      if (p) spawnDamage(p, dmg, head);
      // ⚠️ Счёт здесь больше НЕ увеличиваем. Свои убийства приезжают серверной
      // таблицей (`OP_SCORES` → lobby.js): матч считает их сам, и второй,
      // локальный счётчик разошёлся бы с ним на первом же спорном фраге
      // (добил огонь, задело гранатой) — а показывали бы мы именно локальный.
      break;
    }

    // ---- Попали по нам ----
    case OP.DAMAGE: {
      const hp = d.getUint8(3);
      // HP берём с сервера как есть — это единственный источник правды
      player.hp = hp;
      player.lastHurt = performance.now() / 1000;
      S.shake = Math.min(S.shake + 0.05, 0.1);
      const hf = document.getElementById('hitflash');
      hf.style.opacity = '0.5';
      setTimeout(() => hf.style.opacity = '0', 80);
      playHurt();
      break;
    }

    // ---- Кто-то умер ----
    case OP.KILL: {
      const killer = d.getUint16(1, true);
      const victim = d.getUint16(3, true);
      const head = (d.getUint8(5) & 1) !== 0;
      // Хвост кадра — откуда прилетело и насколько сильно (протокол v8).
      // Здесь только запоминаем: уронить тело сейчас нельзя, оно нарисовано
      // на interpDelay в прошлом (подробности — в corpse.js::pending).
      rememberKill(victim, [
        d.getInt8(6) / 127, d.getInt8(7) / 127, d.getInt8(8) / 127,
      ], d.getUint8(9) / 255);
      const kn = killer === me.id ? 'Вы' : (nicks.get(killer) || `#${killer}`);
      const vn = victim === me.id ? 'Вас' : (nicks.get(victim) || `#${victim}`);
      addKill(`${kn} ${head ? '⊛' : '✖'} ${vn}`);
      break;
    }

    // ---- Боезапас (сервер поправляет наше предсказание) ----
    case OP.AMMO: {
      const key = WEAPON_KEY[d.getUint8(1)];
      const w = WEAPONS[key];
      if (!w) break;
      w.ammo = d.getUint8(2);
      const reloading = d.getUint8(3) !== 0;
      // Сервер говорит «перезарядки нет» — значит её либо закончили раньше нас,
      // либо ОТМЕНИЛИ (бег: `room::apply_input`), либо вовсе не начинали
      // (`room::reload` отверг кадр, пришедший на бегу). Раньше здесь стояло
      // дополнительное условие `w.ammo === w.mag`, то есть отмену клиент
      // пропускал бы: полоска крутилась бы до конца и он считал бы магазин
      // полным, тогда как сервер его не трогал. Проверяем только, что кадр
      // про ТЕКУЩИЙ ствол: после смены оружия приходит кадр нового, а свою
      // перезарядку смена и так отменила.
      if (!reloading && S.reloading && key === S.curKey) cancelReload();
      break;
    }

    // ---- Жив/мёртв решает сервер ----
    case OP.SELF: {
      const alive = (d.getUint8(19) & F_ALIVE) !== 0;
      if (S.netState !== 'online') break;      // офлайн играем по локальным правилам
      player.hp = d.getUint8(20);              // HP считает только сервер
      if (!alive && player.alive) {
        killPlayer();
      } else if (alive && !player.alive) {
        respawnPlayer();
        resetPrediction();                     // история вводов из прошлой жизни не нужна
      }
      break;
    }

    // ---- Чужой выстрел: трассер, искры, дырка от пули ----
    //
    // Кадр приходит от всех, кого СЛЫШНО (тот же отбор, что у OP_SOUND),
    // и несёт две точки: откуда вылетело и во что уткнулось. Именно по нему
    // становится понятно, откуда стреляют: звук даёт направление на слух,
    // трассер — линию и точку вылета глазами.
    //
    // ⚠️ Точка ВЫЛЕТА округлена сервером до полуметровой сетки (античит,
    // см. proto.rs::OP_SHOT). Для трассера это незаметно, а вот считать
    // по ней что-либо точное нельзя — и не надо.
    case OP.SHOT: {
      const ox = unpackPos(d.getInt16(3, true));
      const oy = unpackPos(d.getInt16(5, true));
      const oz = unpackPos(d.getInt16(7, true));
      const ex = unpackPos(d.getInt16(9, true));
      const ey = unpackPos(d.getInt16(11, true));
      const ez = unpackPos(d.getInt16(13, true));
      const flags = d.getUint8(15);
      const from = new THREE.Vector3(ox, oy, oz);
      const to = new THREE.Vector3(ex, ey, ez);
      spawnTracer(from, to, flags & 0b11, false);
      // ⚠️ Протокол 19: пуля прошивает людей, поэтому конец луча — ВСЕГДА
      // стена (или предел дальности), и дырку ставим независимо от того,
      // задела ли пуля кого-то по пути. Раньше бит SHOT_BODY эту дырку как
      // раз и запрещал; теперь он значит лишь «по дороге были люди».
      //
      // Нормаль сервер не присылает (лишние байты в кадре, который уходит
      // всем и часто) — берём её тем же рейкастом, каким клиент и так ищет
      // стены для своего выстрела. Луч короткий: точка попадания уже
      // известна, нужен лишь треугольник в ней.
      const dir = to.clone().sub(from).normalize();
      _ray.set(to.clone().addScaledVector(dir, -0.5), dir);
      _ray.far = 1.2;
      const h = _ray.intersectObjects(wallMeshes, false)[0];
      const nrm = h && h.face
        ? h.face.normal.clone().transformDirection(h.object.matrixWorld)
        : dir.clone().negate();
      spawnSparks(to, nrm, 0xffcc66, 6);
      addDecal(to, nrm);
      // Красные брызги — на каждом, кого пуля прошла. Точек тел в кадре нет
      // (это стоило бы по 6 байт на человека), поэтому ищем их своим лучом
      // по уже нарисованным телам. На урон это не влияет ничем: он посчитан
      // сервером ещё до отправки кадра.
      //
      // Тому же лучу мы верим и в вопросе «стоял ли человек у стены»: если
      // пуля кончилась в стене сразу за ним — на ней остаётся пятно крови.
      // ⚠️ Стену тут даём только когда рейкаст её реально нашёл (`h`): без
      // него `nrm` — это просто «обратно вдоль луча», и по такой нормали
      // пятно легло бы поперёк воздуха на пределе дальности.
      if (flags & 0b100) {
        for (const p of bodiesAlong(from, to)) {
          spawnSparks(p, dir.clone().negate(), 0x991111, 6);
          spawnBloodSplat(p, h ? to : null, nrm);
        }
      }
      break;
    }

    // ---- Ники для killfeed ----
    case OP.JOIN: {
      const id = d.getUint16(1, true);
      const len = d.getUint8(3);
      nicks.set(id, new TextDecoder().decode(new Uint8Array(buf, 4, len)));
      break;
    }
    case OP.LEAVE:
      nicks.delete(d.getUint16(1, true));
      break;
  }
});
